import { useState } from "react";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { ChevronDown, ChevronUp, Eye, EyeOff } from "lucide-react";

// Reusable component for editing a text element's style
export default function TextElementEditor({ 
  label, 
  value, 
  onChange, 
  showTextInput = true,
  showVisibility = false,
  showAlignment = true,
  collapsed = false,
  textPlaceholder = "Enter text...",
  multiline = false
}) {
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  
  const handleChange = (field, newValue) => {
    onChange({ ...value, [field]: newValue });
  };
  
  const fontWeightOptions = [
    { value: "normal", label: "Normal" },
    { value: "bold", label: "Bold" },
  ];
  
  const alignmentOptions = [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
  ];
  
  return (
    <div className="border border-[#E7E5E4] rounded-lg overflow-hidden mb-2">
      {/* Header - clickable */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 bg-[#FAFAF9] hover:bg-[#F5F5F4] transition-colors text-left"
      >
        <span className="text-sm font-medium text-[#44403C]">{label}</span>
        <div className="flex items-center gap-2">
          {showVisibility && (
            <span 
              onClick={(e) => {
                e.stopPropagation();
                handleChange('visible', !value.visible);
              }}
              className="p-1 hover:bg-[#E7E5E4] rounded cursor-pointer"
            >
              {value.visible !== false ? <Eye size={14} /> : <EyeOff size={14} className="text-[#A8A29E]" />}
            </span>
          )}
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      
      {/* Content */}
      {isExpanded && (
        <div className="p-3 space-y-3 bg-white">
          {/* Text input */}
          {showTextInput && (
            <div>
              <label className="text-xs text-[#78716C] mb-1 block">Text</label>
              {multiline ? (
                <Textarea
                  value={value.text || ""}
                  onChange={(e) => handleChange('text', e.target.value)}
                  placeholder={textPlaceholder}
                  className="text-sm min-h-[100px]"
                  rows={4}
                />
              ) : (
                <Input
                  value={value.text || ""}
                  onChange={(e) => handleChange('text', e.target.value)}
                  placeholder={textPlaceholder}
                  className="text-sm"
                />
              )}
            </div>
          )}
          
          {/* Style controls - single row */}
          <div className="flex flex-wrap gap-2 items-end">
            {/* Font Size */}
            <div className="flex-shrink-0">
              <label className="text-xs text-[#78716C] mb-1 block">Size</label>
              <Input
                type="number"
                min="6"
                max="48"
                value={value.fontSize || 10}
                onChange={(e) => handleChange('fontSize', parseInt(e.target.value) || 10)}
                className="w-16 text-sm h-8"
              />
            </div>
            
            {/* Font Weight */}
            <div className="flex-shrink-0">
              <label className="text-xs text-[#78716C] mb-1 block">Weight</label>
              <select
                value={value.fontWeight || "normal"}
                onChange={(e) => handleChange('fontWeight', e.target.value)}
                className="h-8 px-2 text-sm border border-[#E7E5E4] rounded bg-white"
              >
                {fontWeightOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            {/* Font Color */}
            <div className="flex-shrink-0">
              <label className="text-xs text-[#78716C] mb-1 block">Color</label>
              <input
                type="color"
                value={value.fontColor || "#000000"}
                onChange={(e) => handleChange('fontColor', e.target.value)}
                className="w-10 h-8 border border-[#E7E5E4] rounded cursor-pointer"
              />
            </div>
            
            {/* Alignment */}
            {showAlignment && (
              <div className="flex-shrink-0">
                <label className="text-xs text-[#78716C] mb-1 block">Align</label>
                <select
                  value={value.alignment || "left"}
                  onChange={(e) => handleChange('alignment', e.target.value)}
                  className="h-8 px-2 text-sm border border-[#E7E5E4] rounded bg-white"
                >
                  {alignmentOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Margin Bottom */}
            <div className="flex-shrink-0">
              <label className="text-xs text-[#78716C] mb-1 block">Spacing ↓</label>
              <Input
                type="number"
                min="0"
                max="50"
                value={value.marginBottom ?? 0}
                onChange={(e) => handleChange('marginBottom', parseInt(e.target.value) || 0)}
                className="w-14 text-sm h-8"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Simplified version for rows (label + value pair)
export function RowStyleEditor({ label, value, onChange }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const handleChange = (field, newValue) => {
    onChange({ ...value, [field]: newValue });
  };
  
  return (
    <div className="border border-[#E7E5E4] rounded-lg overflow-hidden mb-2">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 bg-[#FAFAF9] hover:bg-[#F5F5F4] transition-colors text-left"
      >
        <span className="text-sm font-medium text-[#44403C]">{label}</span>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      
      {isExpanded && (
        <div className="p-3 space-y-3 bg-white">
          {/* Label style */}
          <div>
            <label className="text-xs text-[#78716C] mb-1 block">Label Style</label>
            <div className="flex gap-2">
              <Input
                type="number" min="6" max="24"
                value={value.labelFontSize || 10}
                onChange={(e) => handleChange('labelFontSize', parseInt(e.target.value) || 10)}
                className="w-16 text-sm h-8"
                placeholder="Size"
              />
              <select
                value={value.labelFontWeight || "normal"}
                onChange={(e) => handleChange('labelFontWeight', e.target.value)}
                className="h-8 px-2 text-sm border border-[#E7E5E4] rounded bg-white"
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
              <input
                type="color"
                value={value.labelFontColor || "#000000"}
                onChange={(e) => handleChange('labelFontColor', e.target.value)}
                className="w-10 h-8 border border-[#E7E5E4] rounded cursor-pointer"
              />
            </div>
          </div>
          
          {/* Value style */}
          <div>
            <label className="text-xs text-[#78716C] mb-1 block">Value Style</label>
            <div className="flex gap-2">
              <Input
                type="number" min="6" max="24"
                value={value.valueFontSize || 10}
                onChange={(e) => handleChange('valueFontSize', parseInt(e.target.value) || 10)}
                className="w-16 text-sm h-8"
                placeholder="Size"
              />
              <select
                value={value.valueFontWeight || "normal"}
                onChange={(e) => handleChange('valueFontWeight', e.target.value)}
                className="h-8 px-2 text-sm border border-[#E7E5E4] rounded bg-white"
              >
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
              <input
                type="color"
                value={value.valueFontColor || "#000000"}
                onChange={(e) => handleChange('valueFontColor', e.target.value)}
                className="w-10 h-8 border border-[#E7E5E4] rounded cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
